import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PlanForm, planPayload, StatusBadge, STATUS } from "../components/app/PlanForm";
import { MonitoringPanel } from "../components/app/MonitoringPanel";
import { EvaluationCard, FeedbackCard } from "../components/app/EvaluationCards";

const STEPS = ["proposed", "under_review", "approved", "in_progress", "completed", "evaluated"];
const ACTION = { under_review: "Send for review", proposed: "Return to proposer", approved: "Approve", in_progress: "Start implementation", completed: "Mark completed", evaluated: "Mark evaluated", deferred: "Defer", cancelled: "Cancel" };
const Card = ({ title, children, testId }) => (
  <section className="rounded-xl border border-amber-700/15 bg-white p-5 sm:p-6" data-testid={testId}>
    <h2 className="mb-3 font-serif-display text-xl font-semibold text-amber-900">{title}</h2>{children}
  </section>
);
const Row = ({ k, v }) => <div className="grid grid-cols-[180px_1fr] gap-3 border-t border-amber-700/10 py-2 text-sm"><span className="text-stone-500">{k}</span><span className="text-stone-800 whitespace-pre-wrap">{v || "—"}</span></div>;

const Stepper = ({ status }) => (
  <ol className="flex flex-wrap gap-2" data-testid="plan-stepper">
    {STEPS.map((s, i) => {
      const done = STEPS.indexOf(status) >= i;
      return <li key={s} className={`rounded-full px-3 py-1 text-xs font-semibold ${done ? "bg-amber-900 text-amber-50" : "bg-[#F5F0E6] text-stone-500"}`}>{i + 1}. {STATUS[s][0]}</li>;
    })}
    {["deferred", "cancelled"].includes(status) && <li><StatusBadge s={status} /></li>}
  </ol>
);

const TextSave = ({ label, value, field, planId, onSaved, testId }) => {
  const [v, setV] = useState(value || "");
  const save = async () => { try { await api.patch(`/plans/${planId}`, { [field]: v }); toast.success("Saved"); onSaved(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="space-y-2"><p className="text-xs font-semibold text-stone-600">{label}</p>
      <Textarea rows={3} value={v} onChange={(e) => setV(e.target.value)} data-testid={`${testId}-input`} />
      <Button size="sm" variant="outline" onClick={save} data-testid={`${testId}-save`}>Save</Button></div>
  );
};

const Details = ({ p }) => (
  <>
    <Row k="Objective" v={p.objective} /><Row k="Expected results" v={p.expected_results} /><Row k="Target population" v={p.target_population} />
    <Row k="Intervention" v={p.intervention} /><Row k="Responsible" v={p.responsible_unit} /><Row k="GKK" v={p.gkk_name} />
    <Row k="Coordinator" v={p.coordinator_name} /><Row k="Location" v={p.location} /><Row k="Dates" v={`${p.start_date || "—"} → ${p.target_date || "—"}`} />
    <Row k="Resources" v={p.resources} /><Row k="Proposed budget" v={p.proposed_budget != null ? `₱${p.proposed_budget.toLocaleString()}` : null} />
    <Row k="Approved budget" v={p.approved_budget != null ? `₱${p.approved_budget.toLocaleString()}` : "Not yet set"} />
    <Row k="Expected beneficiaries" v={p.expected_beneficiaries} />
    <Row k="Indicators" v={(p.indicators || []).map((i) => `${i.name}${i.target != null ? ` (target ${i.target})` : ""}`).join("\n")} />
  </>
);

export default function PlanDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [err, setErr] = useState("");
  const [edit, setEdit] = useState(null);
  const [lists, setLists] = useState({ gkks: [], users: [] });
  const [tr, setTr] = useState(null);
  const [budget, setBudget] = useState("");
  const load = useCallback(() => api.get(`/plans/${id}`).then((r) => { setP(r.data); setBudget(r.data.approved_budget ?? ""); }).catch((e) => setErr(errMsg(e))), [id]);
  useEffect(() => { load(); }, [load]);

  const openEdit = async () => {
    const [g, u] = await Promise.all([api.get("/gkks"), api.get("/users/assignable")]);
    setLists({ gkks: g.data, users: u.data }); setEdit({ ...p });
  };
  const saveEdit = async () => { try { await api.patch(`/plans/${id}`, planPayload(edit)); toast.success("Plan updated"); setEdit(null); load(); } catch (e) { toast.error(errMsg(e)); } };
  const saveBudget = async () => { try { await api.patch(`/plans/${id}`, { approved_budget: budget === "" ? null : Number(budget) }); toast.success("Approved budget saved"); load(); } catch (e) { toast.error(errMsg(e)); } };
  const doTr = async () => { try { await api.post(`/plans/${id}/transition`, { to: tr.to, note: tr.note || "" }); toast.success(`Status: ${STATUS[tr.to][0]}`); setTr(null); load(); } catch (e) { toast.error(errMsg(e)); } };

  if (err) return <p className="text-sm text-stone-600" data-testid="plan-error">{err}</p>;
  if (!p) return null;
  return (
    <div className="space-y-6">
      <Link to="/plans" className="inline-flex items-center gap-1 text-sm text-amber-900" data-testid="back-to-plans"><ArrowLeft className="h-4 w-4" />All action plans</Link>
      <div>
        <p className="text-xs uppercase tracking-widest font-semibold text-amber-800/80">{p.concern} · {p.gkk_name}</p>
        <h1 className="mt-1 font-serif-display text-3xl sm:text-4xl font-bold text-amber-950" data-testid="plan-title">{p.intervention}</h1>
        <div className="mt-4"><Stepper status={p.status} /></div>
      </div>
      <div className="flex flex-wrap gap-2" data-testid="plan-actions">
        {p.can.transitions.map((to) => <Button key={to} variant={["deferred", "cancelled", "proposed"].includes(to) ? "outline" : "default"} className={["deferred", "cancelled", "proposed"].includes(to) ? "" : "bg-amber-900 hover:bg-[#5B210B]"} onClick={() => setTr({ to, note: "" })} data-testid={`transition-${to}`}>{ACTION[to]}</Button>)}
        {p.can.edit_plan && <Button variant="outline" onClick={openEdit} data-testid="edit-plan-button">Edit plan details</Button>}
        {p.can.monitor && <Link to={`/plans/${id}/qr`}><Button variant="outline" data-testid="plan-qr-button">Feedback QR code</Button></Link>}
      </div>
      <div className="grid gap-6 lg:grid-cols-[1.3fr_1fr]">
        <div className="space-y-6">
          <Card title="Identified concern & evidence" testId="plan-evidence"><p className="text-sm text-stone-700">{p.evidence || "No survey evidence attached (leader proposal)."}</p></Card>
          <Card title="Action plan" testId="plan-details"><Details p={p} /></Card>
          <MonitoringPanel plan={p} onSaved={load} />
        </div>
        <div className="space-y-6">
          {p.can.set_budget && (
            <Card title="Approved budget" testId="budget-card">
              <div className="flex gap-2"><Input type="number" min="0" value={budget} onChange={(e) => setBudget(e.target.value)} data-testid="approved-budget-input" /><Button onClick={saveBudget} variant="outline" data-testid="approved-budget-save">Save</Button></div>
              <p className="mt-2 text-xs text-stone-500">Required before approval. Enter 0 if no funds are needed.</p>
            </Card>
          )}
          <Card title="Accomplishments & follow-up" testId="progress-card">
            {p.can.progress ? (
              <div className="space-y-4">
                <TextSave label="Actual accomplishments" value={p.accomplishments} field="accomplishments" planId={id} onSaved={load} testId="accomplishments" />
                <TextSave label="Follow-up requirements" value={p.follow_up} field="follow_up" planId={id} onSaved={load} testId="follow-up" />
              </div>
            ) : <><Row k="Accomplishments" v={p.accomplishments} /><Row k="Follow-up" v={p.follow_up} /></>}
          </Card>
          <EvaluationCard key={`${p.plan_id}-${p.updated_at}`} plan={p} onSaved={load} />
          {["in_progress", "completed", "evaluated"].includes(p.status) && <FeedbackCard plan={p} />}
          <Card title="Decision history" testId="history-card">
            <ul className="space-y-2 text-sm">{[...p.status_history].reverse().map((h, i) => (
              <li key={i} className="border-t border-amber-700/10 pt-2"><StatusBadge s={h.to} /> <span className="text-xs text-stone-500">{h.by} · {new Date(h.at).toLocaleString()}</span>{h.note && <p className="text-xs text-stone-700 mt-1">{h.note}</p>}</li>
            ))}</ul>
          </Card>
        </div>
      </div>
      <Dialog open={!!tr} onOpenChange={(o) => !o && setTr(null)}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>{tr && ACTION[tr.to]}</DialogTitle><DialogDescription>{["deferred", "cancelled", "proposed"].includes(tr?.to) ? "Please document the reason." : "Add a note for the record (optional)."}</DialogDescription></DialogHeader>
          <Textarea rows={3} value={tr?.note || ""} onChange={(e) => setTr({ ...tr, note: e.target.value })} data-testid="transition-note-input" />
          <DialogFooter><Button onClick={doTr} className="bg-amber-900 hover:bg-[#5B210B]" data-testid="transition-confirm">Confirm</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="bg-white max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit plan details</DialogTitle></DialogHeader>
          {edit && <PlanForm form={edit} setForm={setEdit} gkks={lists.gkks} users={lists.users} lockGkk={user.role === "gkk_president"} />}
          <DialogFooter><Button onClick={saveEdit} className="bg-amber-900 hover:bg-[#5B210B]" data-testid="edit-plan-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
